/** @jsxImportSource @emotion/react */
import { Paper, Typography } from '@mui/material';
import BigNumber from 'bignumber.js';
import React, { useContext } from 'react';
import { useTranslation } from 'translation';
import { formatCentsToReadableValue, formatToReadablePercentage } from 'utilities';

import { useGetTreasuryTotals, useGetMintedUmi, GetMintedUmiOutput, useGetNetworkRewardRules } from 'clients/api';

import { Cell, CellGroup } from 'components';
import { AuthContext } from 'context/AuthContext';
import Card from '../../MarketDetails/Card';
import { useStyles } from '../styles';
import TEST_IDS from '../testIds';

interface HeaderProps {
  totalSupplyCents: BigNumber;
  totalBorrowCents: BigNumber;
  availableLiquidityCents: BigNumber;
  totalTreasuryCents: BigNumber;
  userMintedUmiData?: GetMintedUmiOutput;
  networkRewardRules?: {
    rewardRules: {
      generation: string;
      requirement: string;
      rate: string;
      percentage: BigNumber;
      levels: number[];
    }[];
    totalLevels: number;
    baseRewardRate: BigNumber;
    maxRewardRate: BigNumber;
  };
}

export const HeaderUi: React.FC<HeaderProps> = ({
  totalSupplyCents,
  totalBorrowCents,
  availableLiquidityCents,
  totalTreasuryCents,
  userMintedUmiData,
  networkRewardRules,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const netApyPercentage = userMintedUmiData?.stake ? +userMintedUmiData.stake : 0.00;
  const dailyEarningsCents = userMintedUmiData?.arp ? +userMintedUmiData.arp : 0.00;
  const balance = userMintedUmiData?.balance ? +userMintedUmiData.balance : 0.00;
  const Rewards = userMintedUmiData?.Rewards ? +userMintedUmiData.Rewards : 0.00;


  // 账户信息cells
  const accountCells: Cell[] = [
    {
      label: t('account.accountSummary.netApy'),
      value: formatCentsToReadableValue({ value: netApyPercentage }),
      tooltip: t('account.accountSummary.netApyTooltip'),
      color: styles.getNetApyColor({ netApyPercentage }),
    },
    {
      label: t('account.accountSummary.dailyEarnings'),
      value: formatCentsToReadableValue({ value: dailyEarningsCents }),
    },
    {
      label: t('account.accountSummary.totalSupply'),
      value: formatCentsToReadableValue({ value: balance }),
    },
    {
      label: t('account.accountSummary.totalBorrow'),
      value: formatCentsToReadableValue({ value: 0 }),
    },
    {
      label: t('account.accountSummary.ReferralRewards'),
      value: formatCentsToReadableValue({ value: Rewards }),
    },
    {
      label: t('account.accountSummary.TaxLevel'),
      value: formatCentsToReadableValue({ value: 0 }),
    },
  ];
  return (
    <div css={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* 账户统计卡片 */}
      <Paper css={styles.headerRoot}>
        <CellGroup
          cells={accountCells}
          data-testid={TEST_IDS.stats}
        />
      </Paper>

      {/* 网络奖励规则卡片 */}
      {networkRewardRules && networkRewardRules.rewardRules.length > 0 && (
        <Card
          title={t('networkReward.rules.title')}
          stats={[
            {
              label: t('networkReward.rules.totalLevels'),
              value: networkRewardRules.totalLevels.toString(),
            },
            {
              label: t('networkReward.rules.baseRewardRate'),
              value: networkRewardRules.baseRewardRate.toFixed(0),
            },
            {
              label: t('networkReward.rules.maxRewardRate'),
              value: networkRewardRules.maxRewardRate.toFixed(0),
            },
          ]}
        >
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            {networkRewardRules.rewardRules.map((rule) => (
              <div
                key={rule.generation}
                css={{
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                <Typography variant="small2" css={{ color: '#64748B', marginBottom: '4px' }}>
                  {t(`networkReward.rules.generations.${rule.generation}`) || rule.generation}
                </Typography>
                <Typography variant="h6" css={{ color: '#F1F5F9', marginBottom: '8px' }}>
                  {rule.rate}
                </Typography>
                <Typography variant="small2" css={{ color: '#94A3B8', fontSize: '12px' }}>
                  {t(`networkReward.rules.requirements.${rule.requirement}`) || rule.requirement}
                </Typography>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

const Header = () => {
  const { account } = useContext(AuthContext);

  // TODO: handle loading state (see https://app.clickup.com/t/2d4rcee)
  const {
    data: {
      treasuryTotalSupplyBalanceCents,
      treasuryTotalBorrowBalanceCents,
      treasuryTotalAvailableLiquidityBalanceCents,
      treasuryTotalBalanceCents,
    },
  } = useGetTreasuryTotals();

  const { data: userMintedUmiData, isLoading: isGetUserMintedUmiLoading } = useGetMintedUmi(
      {
        accountAddress: account?.address || '',
      },
      {
        enabled: !!account?.address,
      },
  );

  const { data: networkRewardRules } = useGetNetworkRewardRules(
    {
      accountAddress: account?.address || '',
    },
    {
      enabled: !!account?.address,
    },
  );

  const totalSupplyCents = 0;
  const totalBorrowCents = 0;
  return (
    <HeaderUi
      totalSupplyCents={treasuryTotalSupplyBalanceCents}
      totalBorrowCents={treasuryTotalBorrowBalanceCents}
      availableLiquidityCents={treasuryTotalAvailableLiquidityBalanceCents}
      totalTreasuryCents={treasuryTotalBalanceCents}
      userMintedUmiData={userMintedUmiData}
      networkRewardRules={networkRewardRules}
    />
  );
};

export default Header;
